import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { KeyRound, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { api, authHeaders, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const inputCls =
  "w-full rounded-xl border border-brass/20 bg-ink/70 px-4 py-3 text-sm text-ivory placeholder:text-ivory/35 transition-colors focus:border-brass focus:outline-none";
const labelCls = "mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-ivory/60";

const EMPTY = { name: "", theme: "", descriptor: "", event_date: "", ticket_enabled: false, ticket_url: "" };

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("pl_admin_token") || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [events, setEvents] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadEvents = useCallback(
    async (tok = token) => {
      try {
        const res = await api.get("/admin/events", authHeaders(tok));
        setEvents(res.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem("pl_admin_token");
          setToken("");
          setLoginError("Session expired — please log in again.");
        } else {
          toast.error("Couldn't load events");
        }
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) loadEvents();
  }, [token, loadEvents]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await api.post("/admin/login", { password });
      localStorage.setItem("pl_admin_token", res.data.token);
      setToken(res.data.token);
      setPassword("");
    } catch (err) {
      setLoginError(formatApiError(err, "Incorrect password"));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("pl_admin_token");
    setToken("");
    setEvents(null);
    setForm(EMPTY);
    setEditingId(null);
  };

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      name: ev.name,
      theme: ev.theme,
      descriptor: ev.descriptor,
      event_date: ev.event_date,
      ticket_enabled: ev.ticket_enabled,
      ticket_url: ev.ticket_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.ticket_enabled && !form.ticket_url.trim()) {
      toast.error("Add the ticket link, or turn the ticket button off.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      theme: form.theme.trim(),
      descriptor: form.descriptor.trim(),
      event_date: form.event_date,
      ticket_enabled: form.ticket_enabled,
      ticket_url: form.ticket_enabled ? form.ticket_url.trim() : null,
    };
    try {
      if (editingId) {
        await api.put(`/admin/events/${editingId}`, payload, authHeaders(token));
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload, authHeaders(token));
        toast.success("Event added");
      }
      cancelEdit();
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't save the event"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Remove "${ev.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/events/${ev.id}`, authHeaders(token));
      toast.success("Event removed");
      if (editingId === ev.id) cancelEdit();
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't remove the event"));
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="lamp-glow flex min-h-[80vh] items-center px-5 pb-20 pt-36">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="mx-auto w-full max-w-md rounded-2xl border border-brass/15 bg-charcoal/60 p-8 sm:p-10"
        >
          <KeyRound size={28} className="text-brass" aria-hidden="true" />
          <h1 className="mt-4 font-serif text-3xl text-ivory">
            The Lounge <span className="italic text-brass">office.</span>
          </h1>
          <p className="mt-2 text-sm text-ivory/55">
            Hosts only. Enter the admin password to manage upcoming Lounges.
          </p>
          <label htmlFor="admin-password" className={`${labelCls} mt-7`}>
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            autoComplete="current-password"
            required
          />
          {loginError && (
            <p role="alert" data-testid="admin-login-error" className="mt-3 rounded-lg bg-burgundy/20 px-4 py-3 text-sm text-ivory">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loggingIn}
            className="mt-6 w-full rounded-xl bg-burgundy py-3.5 text-sm font-semibold text-ivory transition-colors hover:bg-[#a33847] disabled:opacity-60"
          >
            {loggingIn ? "Checking…" : "Enter the office"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-5xl px-5 pb-24 pt-32 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-brass">
            <span aria-hidden="true" className="mr-2">♣</span>The Lounge office
          </p>
          <h1 className="mt-2 font-serif text-4xl text-ivory">Upcoming Lounges</h1>
        </div>
        <button
          type="button"
          onClick={logout}
          data-testid="admin-logout-button"
          className="inline-flex h-11 items-center gap-2 rounded-xl border border-brass/25 px-5 text-sm text-ivory/75 transition-colors hover:border-brass/60 hover:text-ivory"
        >
          <LogOut size={15} /> Log out
        </button>
      </div>

      <form
        onSubmit={save}
        data-testid="admin-event-form"
        className="mt-10 space-y-5 rounded-2xl border border-brass/15 bg-charcoal/50 p-7 sm:p-9"
      >
        <h2 className="font-serif text-2xl text-ivory">
          {editingId ? "Edit event" : "Add an event"}
        </h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="ev-name" className={labelCls}>Event name *</label>
            <input id="ev-name" data-testid="admin-event-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="The Spring Deal" required />
          </div>
          <div>
            <label htmlFor="ev-date" className={labelCls}>Date *</label>
            <input id="ev-date" data-testid="admin-event-date-input" type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} required />
          </div>
        </div>
        <div>
          <label htmlFor="ev-theme" className={labelCls}>Theme *</label>
          <input id="ev-theme" data-testid="admin-event-theme-input" value={form.theme} onChange={set("theme")} className={inputCls} placeholder="Season opener" required />
        </div>
        <div>
          <label htmlFor="ev-desc" className={labelCls}>Short descriptor *</label>
          <textarea id="ev-desc" data-testid="admin-event-descriptor-input" rows={3} maxLength={400} value={form.descriptor} onChange={set("descriptor")} className={`${inputCls} resize-y`} placeholder="One or two sentences about the night." required />
        </div>
        <div className="flex items-center justify-between rounded-xl border border-brass/15 bg-ink/50 p-4">
          <div>
            <p className="text-sm font-medium text-ivory">Ticket link button</p>
            <p className="text-xs text-ivory/50">Show a "Claim a ticket" button on this event.</p>
          </div>
          <Switch
            checked={form.ticket_enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, ticket_enabled: v }))}
            data-testid="admin-ticket-toggle"
            aria-label="Toggle ticket link button"
          />
        </div>
        {form.ticket_enabled && (
          <div>
            <label htmlFor="ev-url" className={labelCls}>Ticket link URL *</label>
            <input id="ev-url" data-testid="admin-ticket-url-input" type="url" value={form.ticket_url} onChange={set("ticket_url")} className={inputCls} placeholder="https://…" required={form.ticket_enabled} />
          </div>
        )}
        <div className="flex flex-wrap gap-3 pt-1">
          <button
            type="submit"
            data-testid="admin-event-save-button"
            disabled={saving}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-burgundy px-6 text-sm font-semibold text-ivory transition-colors hover:bg-[#a33847] disabled:opacity-60"
          >
            {editingId ? <Pencil size={15} /> : <Plus size={15} />}
            {saving ? "Saving…" : editingId ? "Save changes" : "Add event"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="admin-event-cancel-button"
              className="inline-flex h-11 items-center rounded-xl border border-brass/25 px-6 text-sm text-ivory/75 hover:border-brass/60"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="mt-12 space-y-4">
        {events === null && <p className="text-sm text-ivory/50">Loading events…</p>}
        {events && events.length === 0 && (
          <p data-testid="admin-events-empty" className="rounded-xl border border-brass/15 bg-charcoal/40 p-6 text-sm text-ivory/60">
            No events yet — add the first one above.
          </p>
        )}
        {events &&
          events.map((ev) => (
            <article
              key={ev.id}
              data-testid={`admin-event-row-${ev.id}`}
              className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-brass/15 bg-charcoal/50 p-6"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-brass">
                  {format(new Date(`${ev.event_date}T12:00:00`), "EEEE d MMMM yyyy")}
                </p>
                <h3 className="mt-1 font-serif text-xl text-ivory">{ev.name}</h3>
                <p className="mt-0.5 text-sm text-ivory/55">{ev.theme}</p>
                <p className="mt-2 text-xs text-ivory/45">
                  Ticket button:{" "}
                  {ev.ticket_enabled ? (
                    <span className="text-brass">on — {ev.ticket_url}</span>
                  ) : (
                    "off"
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => startEdit(ev)}
                  data-testid={`admin-edit-${ev.id}`}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-brass/25 px-4 text-sm text-ivory/80 transition-colors hover:border-brass/60"
                >
                  <Pencil size={14} /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => remove(ev)}
                  data-testid={`admin-delete-${ev.id}`}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-burgundy/50 px-4 text-sm text-ivory/80 transition-colors hover:bg-burgundy/25"
                >
                  <Trash2 size={14} /> Remove
                </button>
              </div>
            </article>
          ))}
      </div>
    </div>
  );
};

export default Admin;
